import type { Scope } from './types'

/**
 * Capabilities — what a person may do, one verb at a time.
 *
 * Why not roles: a role bundles decisions somebody else has to live with, and
 * it has no scope. "Approve Norse Term courses" and "see every student's
 * rankings" are different jobs — a principal reviewing courses has no business
 * reading which courses a kid ranked and did not get — and one role name
 * cannot say that.
 *
 * So: a person holds a flat list of `tool.capability@scope` strings, and an
 * admin composes whatever combination a job actually needs. There is no
 * "secretary" role in the code, because every district's secretary is different
 * and the ones here would have to be re-argued in a year.
 *
 * Stored flat, as strings, on purpose. firestore.rules is genuinely bad at
 * walking nested maps but good at `hasAny` on a list, so a permission check is
 * one cheap comparison instead of six lookups — and rules are where this has to
 * be enforced. Nothing outside this file should ever handle the raw strings:
 * use `holds()`, `grantString()` and `parseCap()`.
 *
 *   caps: [
 *     'norseterm.teach@hs',
 *     'norseterm.review@hs',
 *     'admin.staff@district',
 *   ]
 *
 * `@district` is the wildcard: a capability held at district scope applies
 * everywhere. It is the only inheritance rule, matching CLAUDE.md — scopes are
 * a flat list, not a tree.
 */

export const SCOPES: Scope[] = ['district', 'hs', 'ms', 'es', 'athletics', 'fine-arts']

export const SCOPE_LABELS: Record<Scope, string> = {
  district: 'District-wide',
  hs: 'High School',
  ms: 'Middle School',
  es: 'Elementary',
  athletics: 'Athletics',
  'fine-arts': 'Fine Arts',
}

/**
 * Every capability in the product, with the sentence an admin reads when
 * deciding whether to hand it over. Write these from the point of view of the
 * person granting, not the person coding — this text is the permissions UI.
 */
export interface CapDef {
  id: string                 // 'norseterm.teach'
  tool: string               // 'norseterm'
  label: string
  /** What it lets them do, in one line, plainly. */
  detail: string
  /** Shown in red on the grant screen: this one exposes student data. */
  sensitive?: boolean
}

export const CAPABILITIES: CapDef[] = [
  // ---- norseterm ----
  // HS-only in practice. Split five ways because the jobs genuinely are five
  // jobs: the teacher who proposes a course, the person who approves it, the
  // admin who runs placement, whoever owns the calendar, and the office, which
  // keeps track of attendance and where every student is during the week.
  { id: 'norseterm.teach', tool: 'norseterm', label: 'Propose and run a course',
    detail: 'Submit a course, plan the week, and take attendance for their own roster.' },
  { id: 'norseterm.review', tool: 'norseterm', label: 'Approve courses and projects',
    detail: 'Approve or send back course proposals and independent projects, with notes.' },
  { id: 'norseterm.run', tool: 'norseterm', label: 'Run placement',
    detail: 'Import the student list and their course selections, assign blocks, run and balance placement, place students by hand, assign site visits. Sees every student’s choices and schedule.',
    sensitive: true },
  { id: 'norseterm.attend', tool: 'norseterm', label: 'Attendance and whereabouts',
    detail: 'Take and correct attendance for any course, see attendance reports and absence alerts, and see where every student is: rosters, schedules, artifact courses and off-site projects.',
    sensitive: true },
  { id: 'norseterm.configure', tool: 'norseterm', label: 'Manage Norse Term setup',
    detail: 'Deadlines, the week’s dates, placement rules, the bell schedule, and the project exemplars.' },

  // ---- flex pd ----
  // District-wide. Teachers request in their building; each building's
  // principal approves their own staff; learning coaches and the district
  // office see progress; only the district office sets the year's hours, the
  // flex days and the option lists (and only when held at `district`).
  { id: 'flexpd.request', tool: 'flexpd', label: 'Request flex PD',
    detail: 'Plan and submit their own flex PD requests, join collaborations, and see their own hours.' },
  { id: 'flexpd.approve', tool: 'flexpd', label: 'Approve flex PD',
    detail: 'Approve, send back or deny requests from staff in this building, and verify completed PD.' },
  { id: 'flexpd.view', tool: 'flexpd', label: 'See flex PD progress',
    detail: 'See every request and the hours tracker for this building (or every building, at district). Read only.' },
  { id: 'flexpd.configure', tool: 'flexpd', label: 'Manage flex PD setup',
    detail: 'Set each year’s required hours, the flex day calendar, and the option lists. Only works when held district-wide.' },

  // ---- district administration ----
  { id: 'admin.staff', tool: 'admin', label: 'Edit the staff directory',
    detail: 'Add and update people, titles, phone numbers and rooms.' },
  { id: 'admin.grant', tool: 'admin', label: 'Set other people’s permissions',
    detail: 'Hand out and take away everything on this list, but only within their own scopes, and only what they hold themselves.',
    sensitive: true },
]

export const CAP_BY_ID = new Map(CAPABILITIES.map((c) => [c.id, c]))

/* ---------------- roles ---------------- */

/**
 * A role is a named bundle of capabilities **within one tool**. Never a
 * replacement for capabilities and never stored: a person's role is worked out
 * from what they actually hold, so it cannot drift from their real access.
 *
 * Roles are per tool on purpose. Being NorseTerm's Office has nothing to do
 * with what you are in Flex PD, and a principal can be Admin in one tool and
 * hold nothing in another. Granting a role ticks its capabilities; an admin
 * can still add or remove a single capability for an exception, and the person
 * then reads as "Custom" in that tool rather than as a role they no longer fit.
 */
export interface RoleDef {
  id: string                 // 'norseterm.office'
  tool: string               // 'norseterm'
  label: string              // 'Office'
  /** What the job is, in one sentence. Shown on the roles page. */
  summary: string
  /** Every capability this role is made of. All must be held to count. */
  caps: Cap[]
}

export const TOOL_LABELS: Record<string, string> = {
  norseterm: 'NorseTerm',
  flexpd: 'Flex PD',
  admin: 'Administration',
}

/** In order of seniority — the first match is the one a badge leads with. */
export const ROLES: RoleDef[] = [
  { id: 'norseterm.admin', tool: 'norseterm', label: 'Admin',
    summary: 'Approves courses and independent projects; sets the dates, rules and exemplars; imports the student list, puts courses in blocks and runs placement; and sees attendance.',
    caps: ['norseterm.review', 'norseterm.configure', 'norseterm.run', 'norseterm.attend'] },
  { id: 'norseterm.office', tool: 'norseterm', label: 'Office',
    summary: 'Attendance and knowing where students are during Norse Term: takes and corrects attendance, follows up on absences, and sees every course roster, schedule and off-site project.',
    caps: ['norseterm.attend'] },
  { id: 'norseterm.teacher', tool: 'norseterm', label: 'Teacher',
    summary: 'Proposes a course with its artifact options, plans the week, and takes attendance for their own roster.',
    caps: ['norseterm.teach'] },

  { id: 'flexpd.district', tool: 'flexpd', label: 'District office',
    summary: 'Sets each year’s required hours (total and collaborative), the flex day calendar and the option lists, and sees every building’s progress.',
    caps: ['flexpd.configure', 'flexpd.view'] },
  { id: 'flexpd.principal', tool: 'flexpd', label: 'Principal',
    summary: 'Approves, sends back or denies their own staff’s flex PD requests, verifies completed PD, and sees their building’s hours.',
    caps: ['flexpd.approve', 'flexpd.view'] },
  { id: 'flexpd.coach', tool: 'flexpd', label: 'Learning coach',
    summary: 'Sees their building’s flex PD requests and hours tracker, and what people are learning. Read only; never approves.',
    caps: ['flexpd.view'] },
  { id: 'flexpd.teacher', tool: 'flexpd', label: 'Teacher',
    summary: 'Plans and submits their own flex PD, finds collaborators, and tracks their hours against this year’s requirement.',
    caps: ['flexpd.request'] },

  { id: 'admin.permissions', tool: 'admin', label: 'Permissions manager',
    summary: 'Gives people roles and permissions, within the scopes they manage and only what they hold themselves.',
    caps: ['admin.grant'] },
  { id: 'admin.directory', tool: 'admin', label: 'Directory editor',
    summary: 'Keeps the staff directory current: names, titles, phone numbers, rooms.',
    caps: ['admin.staff'] },
]

/**
 * Roles nobody ticks: they come from the student roster the Sheet keeps. A
 * student is somebody on it; a seminar teacher is whoever it names as one.
 */
export const ROSTER_ROLES: Array<{ tool: string; label: string; summary: string }> = [
  { tool: 'norseterm', label: 'Seminar teacher',
    summary: 'Checks off their own seminar students’ independent-project proposals before an admin gives final approval.' },
  { tool: 'norseterm', label: 'Student',
    summary: 'Ranks courses, proposes an independent project and revises it when it comes back with notes.' },
]

/** The tools that have roles, in display order. */
export const ROLE_TOOLS = [...new Set(ROLES.map((r) => r.tool))]

/**
 * The grants to remove when a role is unticked at a scope: its own, minus any
 * that another still-held role needs — so unticking one role never quietly
 * breaks a different one that shares a capability with it.
 *
 * A role wholly inside the one being unticked does not count as "another":
 * Office is inside Admin, so an admin and an admin-who-is-also-office are the
 * same set of grants, and unticking Admin takes all of it.
 */
export function grantsToRemove(held: Grant[], role: RoleDef, scope: Scope): Grant[] {
  const keep = new Set(ROLES
    .filter((r) => r !== role && !r.caps.every((c) => role.caps.includes(c)))
    .filter((r) => r.caps.every((c) => held.includes(grantString(c, scope))))
    .flatMap((r) => r.caps))
  return role.caps.filter((c) => !keep.has(c)).map((c) => grantString(c, scope))
}

/** Does this person hold every capability in a role, at `scope` or anywhere? */
export function holdsRole(caps: Grant[] | undefined, role: RoleDef, scope?: Scope): boolean {
  return role.caps.every((c) => holds(caps, c, scope))
}

/**
 * What to call this person in one tool. A district admin is "District admin"
 * everywhere; anyone holding capabilities that match no whole role is
 * "Custom"; nobody else gets a label at all.
 */
export function roleLabels(
  person: { caps?: Grant[]; districtAdmin?: boolean } | null | undefined,
  tool: string,
): string[] {
  if (!person) return []
  if (person.districtAdmin) return ['District admin']
  const held = ROLES.filter((r) => r.tool === tool && holdsRole(person.caps, r))
  // A role wholly inside another one held is not worth saying: Admin includes
  // running placement, so an admin is "Admin", not "Admin, Office".
  const matched = held
    .filter((r) => !held.some((o) => o !== r && o.caps.length > r.caps.length && r.caps.every((c) => o.caps.includes(c))))
    .map((r) => r.label)
  if (matched.length) return matched
  return holdsAnyForTool(person.caps, tool) ? ['Custom'] : []
}

/* ---------------- the string format ---------------- */

export type Cap = string          // 'norseterm.teach'
export type Grant = string        // 'norseterm.teach@hs'

export const grantString = (cap: Cap, scope: Scope): Grant => `${cap}@${scope}`

export function parseGrant(g: Grant): { cap: Cap; scope: Scope } | null {
  const at = g.lastIndexOf('@')
  if (at < 1) return null
  const cap = g.slice(0, at)
  const scope = g.slice(at + 1) as Scope
  if (!CAP_BY_ID.has(cap) || !SCOPES.includes(scope)) return null
  return { cap, scope }
}

/* ---------------- asking questions ---------------- */

/**
 * Does this person hold `cap`? Pass a scope to ask about one place, or leave it
 * off to ask "anywhere" — which is the right question for showing a menu item.
 *
 * A capability held at district scope answers yes everywhere.
 */
export function holds(caps: Grant[] | undefined, cap: Cap, scope?: Scope): boolean {
  if (!caps?.length) return false
  if (scope) return caps.includes(grantString(cap, scope)) || caps.includes(grantString(cap, 'district'))
  const prefix = cap + '@'
  return caps.some((g) => g.startsWith(prefix))
}

/** Does this person hold any capability at all for a tool? Drives tile visibility. */
export function holdsAnyForTool(caps: Grant[] | undefined, tool: string): boolean {
  const prefix = tool + '.'
  return (caps ?? []).some((g) => g.startsWith(prefix))
}

/** Every scope in which this person holds `cap`. Drives scope filters on lists. */
export function scopesFor(caps: Grant[] | undefined, cap: Cap): Scope[] {
  const prefix = cap + '@'
  return (caps ?? [])
    .filter((g) => g.startsWith(prefix))
    .map((g) => g.slice(prefix.length) as Scope)
    .filter((s) => SCOPES.includes(s))
}

/** Group a person's grants by scope, for showing back what they have. */
export function byScope(caps: Grant[] | undefined): Array<{ scope: Scope; caps: CapDef[] }> {
  const map = new Map<Scope, CapDef[]>()
  for (const g of caps ?? []) {
    const p = parseGrant(g)
    if (!p) continue
    const def = CAP_BY_ID.get(p.cap)
    if (!def) continue
    map.set(p.scope, [...(map.get(p.scope) ?? []), def])
  }
  return SCOPES.filter((s) => map.has(s)).map((s) => ({ scope: s, caps: map.get(s)! }))
}

/**
 * The three guardrails on granting, in one place because they have to agree
 * with firestore.rules exactly. A UI that offers something the rules will
 * reject is worse than one that offers nothing.
 *
 *   1. You need admin.grant in that scope.
 *   2. You cannot hand out a capability you do not hold yourself.
 *   3. District admins bypass both — somebody has to be able to start.
 */
export function canGrant(
  actor: { caps?: Grant[]; districtAdmin?: boolean },
  cap: Cap,
  scope: Scope,
): boolean {
  if (actor.districtAdmin) return true
  if (!holds(actor.caps, 'admin.grant', scope)) return false
  return holds(actor.caps, cap, scope)
}

/** Scopes this person may administer at all — the tabs on the permissions screen. */
export function administrableScopes(actor: { caps?: Grant[]; districtAdmin?: boolean }): Scope[] {
  if (actor.districtAdmin) return SCOPES
  return scopesFor(actor.caps, 'admin.grant')
}

/**
 * A person's grants with one role turned on or off at one scope. Turning off
 * keeps anything another role they still hold needs (grantsToRemove).
 */
export function withRole(held: Grant[] | undefined, role: RoleDef, scope: Scope, on: boolean): Grant[] {
  const caps = held ?? []
  if (!on) {
    const drop = grantsToRemove(caps, role, scope)
    return caps.filter((g) => !drop.includes(g))
  }
  const add = role.caps.map((c) => grantString(c, scope)).filter((g) => !caps.includes(g))
  return [...caps, ...add]
}
